import React from 'react';
import logo from './logo33.png';

const ParticleNetwork = () => {
    const canvasRef = React.useRef(null);

    React.useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let particles = [];

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        window.addEventListener('resize', resize);
        resize();

        class Particle {
            constructor() {
                this.x = Math.random() * canvas.width;
                this.y = Math.random() * canvas.height;
                this.vx = (Math.random() - 0.5) * 0.5;
                this.vy = (Math.random() - 0.5) * 0.5;
                this.size = Math.random() * 2 + 1;
            }
            update() {
                this.x += this.vx;
                this.y += this.vy;
                if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
                if (this.y < 0 || this.y > canvas.height) this.vy *= -1;
            }
            draw() {
                ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        const init = () => {
            particles = [];
            for (let i = 0; i < 50; i++) {
                particles.push(new Particle());
            }
        };

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            for (let i = 0; i < particles.length; i++) {
                particles[i].update();
                particles[i].draw();
                for (let j = i; j < particles.length; j++) {
                    const dx = particles[i].x - particles[j].x;
                    const dy = particles[i].y - particles[j].y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    if (distance < 150) {
                        ctx.beginPath();
                        ctx.strokeStyle = `rgba(255, 255, 255, ${0.1 - distance / 1500})`;
                        ctx.lineWidth = 1;
                        ctx.moveTo(particles[i].x, particles[i].y);
                        ctx.lineTo(particles[j].x, particles[j].y);
                        ctx.stroke();
                    }
                }
            }
            animationFrameId = requestAnimationFrame(animate);
        };

        init();
        animate();

        return () => {
            window.removeEventListener('resize', resize);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, zIndex: 1, pointerEvents: 'none' }} />;
};

const Hero = ({ onContactClick }) => {
    return (
        <section
            id="hero"
            style={{
                backgroundColor: 'var(--accent-slate)',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                color: 'var(--white)',
                paddingTop: '80px',
                overflow: 'hidden'
            }}
        >
            {/* Animated Grid/Mesh Background */}
            <div style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                    linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
                `,
                backgroundSize: '60px 60px',
                animation: 'gridFade 8s ease-in-out infinite alternate',
                zIndex: 0,
                pointerEvents: 'none'
            }} />

            {/* Floating Geometric Shapes */}
            <div style={{
                position: 'absolute',
                top: '15%',
                left: '10%',
                width: '80px',
                height: '80px',
                border: '1px solid rgba(255,255,255,0.04)',
                borderRadius: '4px',
                transform: 'rotate(45deg)',
                animation: 'floatShape1 20s ease-in-out infinite',
                zIndex: 0,
                pointerEvents: 'none'
            }} />
            <div style={{
                position: 'absolute',
                bottom: '20%',
                right: '12%',
                width: '60px',
                height: '60px',
                border: '1px solid rgba(255,255,255,0.035)',
                borderRadius: '50%',
                animation: 'floatShape2 25s ease-in-out infinite',
                zIndex: 0,
                pointerEvents: 'none'
            }} />
            <div style={{
                position: 'absolute',
                top: '60%',
                left: '75%',
                width: '40px',
                height: '40px',
                border: '1px solid rgba(255,255,255,0.03)',
                borderRadius: '2px',
                transform: 'rotate(20deg)',
                animation: 'floatShape3 18s ease-in-out infinite',
                zIndex: 0,
                pointerEvents: 'none'
            }} />

            <ParticleNetwork />

            {/* Central Main Glow */}
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '70vw',
                height: '70vw',
                background: 'radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 65%)',
                pointerEvents: 'none',
                zIndex: 1,
                animation: 'pulse-center 8s infinite ease-in-out'
            }} />

            <div className="container" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                zIndex: 2,
                position: 'relative'
            }}>

                {/* Logo */}
                <div style={{ marginBottom: '2rem', animation: 'fadeIn 1s ease-out', position: 'relative' }}>
                    <img
                        src={logo}
                        alt="AutoMachina Logo"
                        style={{
                            maxWidth: '180px',
                            width: '100%',
                            height: 'auto',
                            filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.1))'
                        }}
                    />
                </div>

                {/* Headline */}
                <h1 className="gradient-text" style={{
                    fontSize: 'clamp(3rem, 6vw, 5.5rem)',
                    fontWeight: 800,
                    lineHeight: 1.1,
                    marginBottom: '1rem',
                    letterSpacing: '-0.03em',
                    animation: 'slideUp 0.8s ease-out 0.2s backwards'
                }}>
                    Intelligence,<br />Humanized.
                </h1>

                {/* Cryptic Message */}
                <div style={{
                    fontSize: 'clamp(0.9rem, 1.5vw, 1.1rem)',
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: 'monospace',
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    marginBottom: '2.5rem',
                    animation: 'crypticFade 3s infinite alternate ease-in-out'
                }}>
                    Something is being <span style={{ animation: 'glitch 0.3s infinite', display: 'inline-block', color: 'rgba(255, 255, 255, 0.8)' }}>synthesized</span> in the digital ether...
                </div>

                {/* Subtext */}
                <p style={{
                    fontSize: 'clamp(1.1rem, 2vw, 1.35rem)',
                    color: '#cbd5e1',
                    maxWidth: '650px',
                    margin: '0 auto 1.5rem',
                    lineHeight: 1.6,
                    fontWeight: 300,
                    animation: 'slideUp 0.8s ease-out 0.4s backwards'
                }}>
                    We build AI-first solutions that integrate seamlessly into your business.<br className="hidden md:block" /> Custom models, scalable components, and strategic consulting.
                </p>

                {/* Scroll Down Mouse Indicator */}
                <a
                    href="#services"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.6rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        textDecoration: 'none',
                        marginTop: '1rem',
                        animation: 'slideUp 0.8s ease-out 0.6s backwards'
                    }}
                    className="hero-scroll-indicator"
                >
                    <span 
                        style={{
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.2em',
                            color: 'rgba(255,255,255,0.4)',
                            fontWeight: 600,
                            transition: 'all 0.3s ease'
                        }}
                        className="hero-scroll-text"
                    >
                        Explore
                    </span>
                    <div 
                        style={{ 
                            width: '30px',
                            height: '50px',
                            borderRadius: '20px',
                            border: '2px solid rgba(255,255,255,0.3)',
                            display: 'flex',
                            justifyContent: 'center',
                            paddingTop: '8px',
                            transition: 'all 0.3s ease'
                        }}
                        className="hero-scroll-mouse"
                    >
                        <div style={{
                            width: '4px',
                            height: '8px',
                            borderRadius: '2px',
                            backgroundColor: 'rgba(255,255,255,0.7)',
                            animation: 'scrollDot 2s ease-in-out infinite'
                        }} />
                    </div>
                </a>
            </div>

            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes pulse-center {
                    0% { opacity: 0.4; transform: translate(-50%, -50%) scale(1); }
                    50% { opacity: 0.7; transform: translate(-50%, -50%) scale(1.1); }
                    100% { opacity: 0.4; transform: translate(-50%, -50%) scale(1); }
                }
                @keyframes crypticFade {
                    0% { opacity: 0.7; transform: scale(0.95); filter: blur(1px); }
                    100% { opacity: 1.0; transform: scale(1); filter: blur(0px); }
                }
                @keyframes glitch {
                    0% { text-shadow: 2px 0 0 rgba(255,0,0,0.5), -2px 0 0 rgba(0,255,255,0.5); }
                    25% { text-shadow: -2px 0 0 rgba(255,0,0,0.5), 2px 0 0 rgba(0,255,255,0.5); }
                    50% { text-shadow: 2px 0 0 rgba(255,0,0,0.5), -2px 0 0 rgba(0,255,255,0.5); }
                    75% { text-shadow: -2px 0 0 rgba(255,0,0,0.5), 2px 0 0 rgba(0,255,255,0.5); }
                    100% { text-shadow: 2px 0 0 rgba(255,0,0,0.5), -2px 0 0 rgba(0,255,255,0.5); }
                }
                @keyframes gridFade {
                    0% { opacity: 0.3; }
                    100% { opacity: 0.8; }
                }
                @keyframes scrollDot {
                    0% { transform: translateY(0); opacity: 0; }
                    50% { transform: translateY(12px); opacity: 1; }
                    100% { transform: translateY(22px); opacity: 0; }
                }
                @keyframes floatShape1 {
                    0%, 100% { transform: rotate(45deg) translate(0, 0); opacity: 0.5; }
                    25% { transform: rotate(50deg) translate(15px, -20px); opacity: 0.8; }
                    50% { transform: rotate(45deg) translate(-10px, 15px); opacity: 0.5; }
                    75% { transform: rotate(40deg) translate(20px, 10px); opacity: 0.7; }
                }
                @keyframes floatShape2 {
                    0%, 100% { transform: translate(0, 0); opacity: 0.5; }
                    33% { transform: translate(-20px, -15px); opacity: 0.7; }
                    66% { transform: translate(15px, 20px); opacity: 0.4; }
                }
                @keyframes floatShape3 {
                    0%, 100% { transform: rotate(20deg) translate(0, 0); opacity: 0.4; }
                    50% { transform: rotate(25deg) translate(-15px, -20px); opacity: 0.7; }
                }

                /* Scroll indicator hover states */
                .hero-scroll-indicator:hover .hero-scroll-text {
                    color: rgba(255,255,255,0.9) !important;
                    text-shadow: 0 0 8px rgba(255,255,255,0.2);
                }
                .hero-scroll-indicator:hover .hero-scroll-mouse {
                    border-color: rgba(255,255,255,0.6) !important;
                    box-shadow: 0 0 15px rgba(255,255,255,0.1);
                    transform: translateY(2px);
                }
            `}</style>
        </section>
    );
};

export default Hero;
